import { useCallback, useState, useEffect, useRef, RefObject } from 'react';
import { toast } from 'react-toastify';

import * as P from './Profile.style';
import theme from '@styles/theme';
import { onChangeEventType } from '@utils/types';
import { onlyNumber, autofocusHandler } from '@utils/common';
import { getCookie } from '@utils/cookie';
import { validateProfile } from '@utils/validation';
import { PROFILE_DATA } from '@lib/DUMMY';

import {
    Description,
    FlexBox,
    TextButton
} from '@components/styled/StyledComponents';
import StyledInput from '@components/styled/StyledInput';
import StyledPassword from '@components/styled/StyledPassword';
import StyledButton from '@components/styled/StyledButton';
import StyledCheckbox from '@components/styled/StyledCheckbox';
import StyledPostcode from '@components/styled/StyledPostcode';
import StyledSelect from '@components/styled/StyledSelect';
import SecedeConfirmModal from '@components/share/Modal/SecedeConfirmModal';
import ChangePasswordModal from '@components/share/Modal/ChangePasswordModal';

type Inputs = {
    email: string;
    username: string;
    license: string;
    hospitalName: string;
    password: string;
    phoneNum1: string;
    phoneNum2: string;
    phoneNum3: string;
    faxNum1: string;
    faxNum2: string;
    faxNum3: string;
    exponentNum1: string;
    exponentNum2: string;
    exponentNum3: string;
    address: string;
    zipcode: string;
    addressDetail: string;
    chart: string;
};

export type ProfileErrors = {
    [key in keyof Inputs]?: string;
};

const Profile = () => {
    // 번호 input autofocus
    const phoneRef1 = useRef<HTMLInputElement>(null);
    const phoneRef2 = useRef<HTMLInputElement>(null);
    const faxRef1 = useRef<HTMLInputElement>(null);
    const faxRef2 = useRef<HTMLInputElement>(null);
    const exponentRef1 = useRef<HTMLInputElement>(null);
    const exponentRef2 = useRef<HTMLInputElement>(null);
    // 번호 여부 체크박스
    const [hasPhoneNum, setHasPhoneNum] = useState(false);
    const [hasFaxNum, setHasFaxNum] = useState(false);
    const [secedeModal, setSecedeModal] = useState(false);
    const [passwordModal, setPasswordModal] = useState(false);
    const [errors, setErrors] = useState<ProfileErrors>({});
    const [inputs, setInputs] = useState<Record<string, string>>({
        email: '',
        username: '',
        license: '',
        hospitalName: '',
        password: '',
        phoneNum1: '',
        phoneNum2: '',
        phoneNum3: '',
        faxNum1: '',
        faxNum2: '',
        faxNum3: '',
        exponentNum1: '',
        exponentNum2: '',
        exponentNum3: '',
        address: '',
        zipcode: '',
        addressDetail: '',
        chart: '차트 선택'
    });

    // TODO: 임시 데이터
    useEffect(() => {
        if (PROFILE_DATA) {
            const {
                email,
                username,
                license,
                hospitalName,
                phoneNum1,
                phoneNum2,
                phoneNum3,
                faxNum1,
                faxNum2,
                faxNum3,
                exponentNum1,
                exponentNum2,
                exponentNum3,
                address,
                zipcode,
                addressDetail,
                chart
            } = PROFILE_DATA;
            setInputs((prev) => {
                return {
                    ...prev,
                    email,
                    username,
                    license,
                    hospitalName,
                    phoneNum1,
                    phoneNum2,
                    phoneNum3,
                    faxNum1,
                    faxNum2,
                    faxNum3,
                    exponentNum1,
                    exponentNum2,
                    exponentNum3,
                    address,
                    zipcode,
                    addressDetail,
                    chart
                };
            });
            setHasPhoneNum(phoneNum1 ? false : true);
            setHasFaxNum(faxNum1 ? false : true);
        }
    }, []);

    // 없음 선택 시 전화번호 초기화
    useEffect(() => {
        if (hasPhoneNum) {
            setInputs((prev) => {
                return {
                    ...prev,
                    phoneNum1: '',
                    phoneNum2: '',
                    phoneNum3: ''
                };
            });
        }
    }, [hasPhoneNum]);

    // 없음 선택 시 팩스 번호 초기화
    useEffect(() => {
        if (hasFaxNum) {
            setInputs((prev) => {
                return {
                    ...prev,
                    faxNum1: '',
                    faxNum2: '',
                    faxNum3: ''
                };
            });
        }
    }, [hasFaxNum]);

    // 유효성 에러 scroll 및 focus
    useEffect(() => {
        if (Object.keys(errors)[0]) {
            const input = document.getElementById(Object.keys(errors)[0]);
            input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            input?.focus();
        }
    }, [errors]);

    const onChange = useCallback((e: onChangeEventType) => {
        const { name, value } = e.target;
        // 번호만
        if (name.includes('Num')) {
            return setInputs((prev) => {
                return {
                    ...prev,
                    [name]: onlyNumber(value)
                };
            });
        }
        setInputs((prev) => {
            return {
                ...prev,
                [name]: value
            };
        });
    }, []);

    const onSubmit = async () => {
        if (!validateProfile(inputs, hasPhoneNum, hasFaxNum, setErrors))
            return toast.error('정보를 다시 확인해주세요.');
        // TODO: 추후 비밀번호 체크 리졸버 연결

        try {
            toast.success('저장되었습니다.');
        } catch (err) {
            console.error(err);
        }
    };

    const handleSecede = async () => {
        // TODO: 추후 리졸버 추가
        try {
            setSecedeModal(false);
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <P.Wrapper $margin="80px auto 200px">
            <P.Title>내 프로필</P.Title>
            <StyledInput
                id="email"
                title="이메일(아이디)"
                name="email"
                value={inputs.email}
                onChange={onChange}
                placeholder="이메일을 입력해주세요"
                error={!!errors.email}
                maxLength={50}
                margin="0 0 20px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
                disabled
            />
            <StyledInput
                id="username"
                title="대표자명"
                subTitle="필수"
                subTitleColor={theme.colors.pointBlue}
                name="username"
                value={inputs.username}
                onChange={onChange}
                placeholder="이름을 입력해주세요"
                error={!!errors.username}
                maxLength={12}
                margin="20px 0"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
            />
            <StyledInput
                id="license"
                title="요양기관번호"
                name="license"
                value={inputs.license}
                onChange={onChange}
                placeholder="요양기관번호를 입력해주세요"
                error={!!errors.license}
                maxLength={8}
                margin="0 0 20px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
                disabled
            />
            <StyledInput
                id="hospitalName"
                title="병원명"
                subTitle="필수"
                subTitleColor={theme.colors.pointBlue}
                name="hospitalName"
                value={inputs.hospitalName}
                onChange={onChange}
                placeholder="병원명을 입력해주세요"
                error={!!errors.hospitalName}
                maxLength={20}
                margin="0 0 20px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
            />
            <FlexBox
                $justifyContent="space-between"
                $alignItems="flex-end"
                $margin="0 0 8px"
            >
                <StyledInput
                    id="phoneNum1"
                    title="대표 전화번호"
                    name="phoneNum1"
                    value={inputs.phoneNum1}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            phoneRef1 as RefObject<HTMLInputElement>
                        );
                    }}
                    maxLength={3}
                    placeholder="선택"
                    error={!!errors.phoneNum1}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasPhoneNum}
                />
                <P.Hyphen>-</P.Hyphen>
                <StyledInput
                    id="phoneNum2"
                    name="phoneNum2"
                    value={inputs.phoneNum2}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            phoneRef2 as RefObject<HTMLInputElement>
                        );
                    }}
                    error={!!errors.phoneNum2}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasPhoneNum}
                    ref={phoneRef1}
                />
                <P.Hyphen>-</P.Hyphen>
                <StyledInput
                    id="phoneNum3"
                    name="phoneNum3"
                    value={inputs.phoneNum3}
                    onChange={onChange}
                    error={!!errors.phoneNum3}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasPhoneNum}
                    ref={phoneRef2}
                />
            </FlexBox>
            <StyledCheckbox
                checkboxId="hasPhoneNum"
                name="hasPhoneNum"
                label="없음"
                value={false}
                checked={hasPhoneNum}
                onChange={() => setHasPhoneNum((prev) => !prev)}
                margin="0 0 20px"
            />
            <FlexBox
                $justifyContent="space-between"
                $alignItems="flex-end"
                $margin="0 0 8px"
            >
                <StyledInput
                    id="faxNum1"
                    title="팩스"
                    name="faxNum1"
                    value={inputs.faxNum1}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            faxRef1 as RefObject<HTMLInputElement>
                        );
                    }}
                    error={!!errors.faxNum1}
                    maxLength={3}
                    placeholder="선택"
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasFaxNum}
                />
                <P.Hyphen>-</P.Hyphen>
                <StyledInput
                    id="faxNum2"
                    name="faxNum2"
                    value={inputs.faxNum2}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            faxRef2 as RefObject<HTMLInputElement>
                        );
                    }}
                    error={!!errors.faxNum2}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasFaxNum}
                    ref={faxRef1}
                />
                <P.Hyphen>-</P.Hyphen>
                <StyledInput
                    id="faxNum3"
                    name="faxNum3"
                    value={inputs.faxNum3}
                    onChange={onChange}
                    error={!!errors.faxNum3}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasFaxNum}
                    ref={faxRef2}
                />
            </FlexBox>
            <StyledCheckbox
                checkboxId="hasFaxNum"
                name="hasFaxNum"
                label="없음"
                value={false}
                checked={hasFaxNum}
                onChange={() => setHasFaxNum((prev) => !prev)}
                margin="0 0 20px"
            />
            <FlexBox
                $justifyContent="space-between"
                $alignItems="flex-end"
                $margin="0 0 20px"
            >
                <StyledInput
                    id="exponentNum1"
                    title="대표 원장 전화번호"
                    name="exponentNum1"
                    value={inputs.exponentNum1}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            exponentRef1 as RefObject<HTMLInputElement>
                        );
                    }}
                    error={!!errors.exponentNum1}
                    maxLength={3}
                    placeholder="선택"
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                />
                <P.Hyphen>-</P.Hyphen>
                <StyledInput
                    id="exponentNum2"
                    name="exponentNum2"
                    value={inputs.exponentNum2}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            exponentRef2 as RefObject<HTMLInputElement>
                        );
                    }}
                    error={!!errors.exponentNum2}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    ref={exponentRef1}
                />
                <P.Hyphen>-</P.Hyphen>
                <StyledInput
                    id="exponentNum3"
                    name="exponentNum3"
                    value={inputs.exponentNum3}
                    onChange={onChange}
                    error={!!errors.exponentNum3}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    ref={exponentRef2}
                />
            </FlexBox>
            <StyledPostcode
                id="address"
                value={inputs.address}
                setInputs={setInputs}
                error={!!errors.address}
                borderRadius={0}
                borderColor={theme.colors.lightGrayBorder}
                margin="0 0 8px"
                BUTTON
                // disabled
            />
            <StyledInput
                name="addressDetail"
                value={inputs.addressDetail}
                onChange={onChange}
                placeholder="상세주소"
                maxLength={80}
                margin="0 0 20px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
            />
            <StyledSelect
                id="chart"
                title="차트"
                name="chart"
                value={inputs.chart}
                options={['차트 선택', '이지스', '기타']}
                onChange={onChange}
                placeholder="차트 선택"
                error={!!errors.chart}
                margin="0 0 8px"
                borderRadius={0}
                borderColor={theme.colors.lightGrayBorder}
                disabled
                COLUMN
            />
            <Description $margin="0 0 20px" $fontSize={14}>
                차트 변경의 경우 관리자에게 문의해주세요
            </Description>
            <StyledPassword
                id="password"
                title="비밀번호 확인"
                name="password"
                value={inputs.password}
                onChange={onChange}
                placeholder="현재 비밀번호를 입력해주세요"
                error={!!errors.password}
                margin="0 0 8px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
            />
            <FlexBox $justifyContent="flex-end">
                {/* TODO: 추후 액션 연결 필요 */}
                <TextButton
                    onClick={() => setPasswordModal(true)}
                    $fontFamily="PretendardRegular"
                >
                    비밀번호 변경하기
                </TextButton>
            </FlexBox>
            <StyledButton
                title="저장하기"
                margin="32px 0"
                width={343}
                fontSize={14}
                onClick={onSubmit}
                borderRadius={0}
                bgColor={theme.colors.pointBlue}
            />
            <FlexBox $justifyContent="center">
                <TextButton
                    onClick={() => setSecedeModal(true)}
                    $fontFamily="PretendardRegular"
                >
                    서비스 탈퇴하기
                </TextButton>
            </FlexBox>
            {secedeModal && (
                <SecedeConfirmModal
                    isOpen={secedeModal}
                    setIsOpen={setSecedeModal}
                    handleSecede={handleSecede}
                />
            )}
            {passwordModal && (
                <ChangePasswordModal
                    title="비밀번호 변경"
                    firstLogin={false}
                    changePw={passwordModal}
                    setChangePw={setPasswordModal}
                />
            )}
        </P.Wrapper>
    );
};

export default Profile;
